import { Printer } from 'react-feather';
import {
  formatCurrencyV2,
  formatDate,
  getItemById,
  getQueryString,
  OPTIONS,
} from '@/utils/common.util';
import useSWR from 'swr';
import {
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
} from 'carbon-components-react';
import PrintOutHead from '@/components/PrintOutHead';
import { PrintView } from '@/components/PrintView/print-view.component';
import CustomButton from '@/components/custom-button/custom-btn.component';
import Combobox from '@/components/combobox/Combobox';
import PageLayout from '@/components/PageLayout';
import ExportReportButton from '@/components/ExportReportButton';
import { useUrlState } from '@/utils/useUrlState';
import { useDebounceState } from '@/utils/useDebounceState';
import { useClassLabels } from '@/api/class.service';

const initialData = {
  startDate: '',
  endDate: '',
  studentId: '',
  academicYearId: '',
  termId: '',
  classId: '',
  classLabelId: '',
  studentStatus: '',
  isActive: '',
  financialAidId: '',
  ledgerAccountId: '',
};

export default function FinancialAidGrantedReport() {
  const [query, setQuery] = useUrlState(initialData);

  const [searchStudent, setSearchStudent] = useDebounceState('');
  const { data: students, isLoading: isStudentsLoading } = useSWR(
    `student/list?status=Enrolled&${getQueryString({
      query: searchStudent,
    })}`,
  );

  const { data: academicYears } = useSWR(
    'academic-year/list/?skip=0&take=100&query=',
  );
  const { data: terms } = useSWR(
    query.academicYearId
      ? `academic-year/terms/?academicYearId=${query.academicYearId}`
      : null,
  );
  const { data: classes } = useSWR('class/list');
  const { allClassLabels } = useClassLabels();

  const { data: aidPrograms } = useSWR(
    `financial-aid/programs?${getQueryString({
      academicYearId: query.academicYearId,
      termId: query.termId || undefined,
    })}`,
  );

  const { data: billItems } = useSWR(`billing/fees/`);

  const mainUrl = `financial-aid/reports/granted`;
  const {
    data: report,
    isLoading,
    error,
  } = useSWR(
    query.academicYearId ? `${mainUrl}?${getQueryString(query)}` : null,
  );

  const student = getItemById(students, query.studentId);

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Billing', to: '/billing' },
        { label: 'Financial Aid', to: '/financial-aid' },
        { label: `Financial Aid Granted Report`, active: true },
      ]}
      title={
        <div className="d-flex align-items-center" style={{ gap: '30px' }}>
          <h1 className="h-20">Financial Aid Granted Report</h1>
        </div>
      }
      isLoading={isLoading}
    >
      {/* FILTERS  */}
      <p className="mt-20 text-12">
        Select at least an academic year to show report.
      </p>
      <div className="mt-10 card-row-5">
        <Select
          labelText="Academic Year"
          onChange={(e) =>
            setQuery({
              academicYearId: e.target.value,
              termId: '',
              financialAidId: '',
            })
          }
          value={query.academicYearId}
        >
          <SelectItem value="" text="All" />
          {academicYears?.map((year) => (
            <SelectItem key={year.id} value={year.id} text={year.description} />
          ))}
        </Select>
        <Select
          labelText="Term"
          disabled={!query.academicYearId}
          onChange={(e) =>
            setQuery({ termId: e.target.value, financialAidId: '' })
          }
          value={query.termId}
        >
          <SelectItem value="" text="All" />
          {terms?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>

        {/* <Combobox
          options={students ?? []}
          label="Select a Student"
          customStyles={{ width: '300px' }}
          value={
            query.studentId
              ? students?.find((s) => s.id === Number(query.studentId))
              : null
          }
          onChange={(option) => {
            setQuery({ studentId: option ? option.id : '' });
            setSearchStudent('');
          }}
          onSearch={setSearchStudent} // The combobox now just updates the search term state
          onClear={() => {
            setQuery({ studentId: '' });
            setSearchStudent('');
          }}
          loading={isStudentsLoading} // Pass the loading state down
          renderSelected={(option) => `${option.firstName} ${option.lastName}`}
          renderOption={(option) => `${option.firstName} ${option.lastName}`}
          labelKey="fullName"
          valueKey="id"
          searchPlaceholder="Search students..."
          placeholder=""
        /> */}
        <Select
          labelText="Class"
          onChange={(e) =>
            setQuery({ classId: e.target.value, classLabelId: '' })
          }
          value={query.classId}
        >
          <SelectItem value="" text="All" />
          {classes?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>
        <Select
          labelText="Class label"
          onChange={(e) => setQuery({ classLabelId: e.target.value })}
        >
          <SelectItem selected value="" text="All" />
          {allClassLabels?.map((item) => (
            <SelectItem text={item.description} value={item.id} />
          ))}
        </Select>
        <Select
          labelText="Student Status"
          onChange={(e) => setQuery({ studentStatus: e.target.value })}
        >
          <SelectItem value="" text="All" />
          {OPTIONS.enrolled.map((status) => (
            <SelectItem key={status} value={status} text={status} />
          ))}
        </Select>
        <Select
          labelText="Financial Aid"
          onChange={(e) => setQuery({ financialAidId: e.target.value })}
          value={query.financialAidId}
          disabled={!query.academicYearId}
        >
          <SelectItem value="" text="All" />
          {aidPrograms?.map((program) => (
            <SelectItem
              key={program.id}
              value={program.id}
              text={program.name}
            />
          ))}
        </Select>
        <Select
          labelText="Bill Item"
          onChange={(e) => setQuery({ ledgerAccountId: e.target.value })}
          value={query.ledgerAccountId}
        >
          <SelectItem value="" text="All" />
          {billItems?.map((item) => (
            <SelectItem
              key={item.id}
              value={item.id}
              text={`${item.ledgerAccountDescription} ${item.classDescription ? `(${item.classDescription})` : ''}`}
            />
          ))}
        </Select>
        <Select
          labelText="Aid Status"
          onChange={(e) => setQuery({ isActive: e.target.value })}
          value={query.isActive}
        >
          <SelectItem value="" text="All" />
          <SelectItem value="true" text="Active" />
          <SelectItem value="false" text="Inactive" />
        </Select>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="start-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                startDate: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ startDate: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="start-date"
            id="start-date"
            name="startDate"
            labelText="Start Date"
            value={query.startDate}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ startDate: e.target.value })}
          />
        </DatePicker>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="end-date-picker"
          // minDate={
          //   new Date(query.startDate ?? '').toISOString().split('T')[0]
          // }
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                endDate: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
              });
            } else setQuery({ endDate: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="end-date"
            id="end-date"
            name="endDate"
            labelText="End Date"
            value={query.endDate}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) => setQuery({ endDate: e.target.value })}
          />
        </DatePicker>
      </div>

      <div className="mt-20">
        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}
        {report?.length > 0 && (
          <>
            <PrintOutHead
              title={`Financial Aid Granted Report`}
              headings={[
                {
                  label: 'Financial Aid',
                  value:
                    getItemById(aidPrograms, query.financialAidId)?.name ??
                    'All',
                },
                {
                  label: 'Bill Item',
                  value:
                    getItemById(billItems, query.ledgerAccountId)
                      ?.ledgerAccountDescription ?? 'All',
                },

                {
                  label: 'Academic Year',
                  value:
                    getItemById(academicYears, query.academicYearId)
                      ?.description ?? 'All',
                },

                {
                  label: 'Term',
                  value: getItemById(terms, query.termId)?.description ?? 'All',
                },
                {
                  label: 'Class',
                  value:
                    getItemById(classes, query.classId)?.description ?? 'All',
                },
                // {
                //   label: 'Student',
                //   value: `${student?.firstName || ''} ${student?.lastName || 'All'}`,
                // },
                {
                  label: 'Student Status',
                  value: query?.studentStatus ?? 'All',
                },
                {
                  label: 'Date Range',
                  value: query.startDate
                    ? `${formatDate(query.startDate)} - ${formatDate(
                        query.endDate,
                      )} `
                    : '-',
                },
              ]}
            />
            <PrintView>
              <div style={{ overflow: 'auto' }}>
                <table className="bx--data-table table-white">
                  <thead>
                    <tr>
                      <th>Academic Year</th>
                      <th>Term</th>
                      <th>Date</th>
                      <th>Student Name</th>
                      <th>Class</th>
                      <th>Class Label</th>
                      <th>Program Name</th>
                      <th>Type</th>
                      <th>Billing Item</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.map((entry) => {
                      return (
                        <tr key={entry.id}>
                          <td>{entry.academicYear}</td>
                          <td>{entry.term}</td>
                          <td>{formatDate(entry.date)}</td>
                          <td>{entry.studentName}</td>
                          <td>{entry.class}</td>
                          <td>{entry.classLabel}</td>
                          <td>{entry.programName}</td>
                          <td>{entry.programType}</td>
                          <td>{entry.billingItem}</td>
                          <td>
                            {entry?.amountType === 'Fixed'
                              ? formatCurrencyV2(entry.amount)
                              : `${entry.amount}%`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </PrintView>
          </>
        )}
        {report?.length === 0 && (
          <p className="text-14 mt-30 text-grey">No data available.</p>
        )}

        {report?.length > 0 && (
          <div className="mt-30 d-flex gap-20 align-items-center flex-end">
            <ExportReportButton
              apiUrl={`${mainUrl}/export?${getQueryString(query)}`}
              fileName={`Financial Aid Granted Report.xlsx`}
            />
            <CustomButton classes="secondary" onClick={() => window.print()}>
              <Printer size="16" strokeWidth="2px" /> &nbsp; Print
            </CustomButton>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
